import React, { useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import Modal from './Modal';
import { useLanguage } from '../i18n/LanguageProvider';

const SetupWizard = ({ clinic, onComplete, onLogout }) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [areaCode, setAreaCode] = useState('689');
  const [provisioningStatus, setProvisioningStatus] = useState('');
  
  // Modal states
  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    confirmText: 'OK',
    showCancel: false
  });

  const steps = [
    {
      id: 1,
      title: t('setupWizard.welcome'),
      description: t('setupWizard.welcomeDescription'),
      icon: '👋',
      content: (
        <div className="text-center">
          <div className="text-8xl mb-8">🏥</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            {t('setupWizard.welcomeToClinic', { clinicName: clinic?.clinic_name })}
          </h3>
          <p className="text-xl text-gray-600 mb-8">
            {t('setupWizard.setupDescription')}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-left max-w-2xl mx-auto">
            <h4 className="font-semibold text-blue-900 mb-4 text-lg">{t('setupWizard.whatWellSetup')}</h4>
            <ul className="text-blue-800 space-y-3">
              <li className="flex items-center">
                <span className="text-2xl mr-3">✅</span>
                <span>{t('setupWizard.createAiAssistant')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">✅</span>
                <span>{t('setupWizard.provisionPhoneNumber')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">✅</span>
                <span>{t('setupWizard.configureSystemPrompt')}</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: t('setupWizard.createAssistant'),
      description: t('setupWizard.creatingAssistant'),
      icon: '🤖',
      content: (
        <div className="text-center">
          <div className="text-8xl mb-8">🤖</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            {t('setupWizard.creatingYourAiAssistant')}
          </h3>
          <p className="text-xl text-gray-600 mb-8">
            {t('setupWizard.assistantDescription')}
          </p>
          {loading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mr-4"></div>
              <span className="text-xl text-blue-600">{t('setupWizard.creatingAssistantLoading')}</span>
            </div>
          )}
        </div>
      )
    },
    {
      id: 3,
      title: t('setupWizard.phoneNumber'),
      description: t('setupWizard.gettingPhoneNumber'),
      icon: '📞',
      content: (
        <div className="text-center">
          <div className="text-8xl mb-8">📞</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            {t('setupWizard.provisionPhoneNumberTitle')}
          </h3>
          <p className="text-xl text-gray-600 mb-8">
            {t('setupWizard.chooseAreaCode')}
          </p>
          
          <div className="max-w-md mx-auto">
            <label className="block text-lg font-medium text-gray-700 mb-4">
              {t('setupWizard.areaCode')}
            </label>
            <select
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value)}
              className="w-full px-6 py-4 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="689">689</option>
              <option value="447">447</option>
              <option value="539">539</option>
            </select>
          </div>

          {provisioningStatus && (
            <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg max-w-md mx-auto">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-lg text-blue-700">{provisioningStatus}</span>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      id: 4,
      title: t('setupWizard.systemPrompt'),
      description: t('setupWizard.configuringAssistant'),
      icon: '💬',
      content: (
        <div className="text-center">
          <div className="text-8xl mb-8">💬</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            {t('setupWizard.systemPromptReady')}
          </h3>
          <p className="text-xl text-gray-600 mb-8">
            {t('setupWizard.promptConfigured')}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-left max-w-2xl mx-auto">
            <h4 className="font-semibold text-green-900 mb-4 text-lg">{t('setupWizard.defaultConfiguration')}</h4>
            <ul className="text-green-800 space-y-3">
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.professionalMedicalAssistant')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.handlesGeneralInquiries')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.directsEmergenciesTo911')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.refersMedicalQuestions')}</span>
              </li>
            </ul>
            <p className="text-sm text-green-700 mt-4">
              {t('setupWizard.customizeLater')}
            </p>
          </div>
        </div>
      )
    },
    {
      id: 5,
      title: t('setupWizard.complete'),
      description: t('setupWizard.assistantReady'),
      icon: '🎉',
      content: (
        <div className="text-center">
          <div className="text-8xl mb-8">🎉</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            {t('setupWizard.setupComplete')}
          </h3>
          <p className="text-xl text-gray-600 mb-8">
            {t('setupWizard.assistantReadyDescription')}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-left max-w-2xl mx-auto">
            <h4 className="font-semibold text-green-900 mb-4 text-lg">{t('setupWizard.whatsNext')}</h4>
            <ul className="text-green-800 space-y-3">
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.monitorCallLogs')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.customizeSystemPrompt')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.uploadKnowledgeFiles')}</span>
              </li>
              <li className="flex items-center">
                <span className="text-2xl mr-3">•</span>
                <span>{t('setupWizard.assistantReadyToTakeCalls')}</span>
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  const handleCreateAssistant = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      await axios.post(`${API_BASE_URL}/api/assistant/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentStep(3); // Move to phone number step
    } catch (error) {
      console.error('Failed to create assistant:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        onLogout();
        return;
      }
      setError(error.response?.data?.error || t('setupWizard.failedToCreateAssistant'));
      setModal({
        isOpen: true,
        title: t('common.error'),
        message: error.response?.data?.error || t('setupWizard.failedToCreateAssistant'),
        type: 'error',
        onConfirm: null,
        confirmText: t('common.ok'),
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProvisionPhone = async () => {
    try {
      setLoading(true);
      setError('');
      setProvisioningStatus(t('setupWizard.tryingAreaCode', { areaCode }));
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_BASE_URL}/api/phone/provision`, {
        areaCode: areaCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProvisioningStatus('');
      setCurrentStep(4); // Move to system prompt step
      
      // Show success message with fallback info if applicable
      const fallbackInfo = response.data.fallbackInfo;
      if (fallbackInfo && fallbackInfo.wasFallback) {
        setModal({
          isOpen: true,
          title: t('setupWizard.phoneProvisioned'),
          message: t('setupWizard.phoneProvisionedMessage', {
            requestedAreaCode: fallbackInfo.requestedAreaCode,
            successfulAreaCode: fallbackInfo.successfulAreaCode
          }),
          type: 'success',
          onConfirm: null,
          confirmText: t('common.ok'),
          showCancel: false
        });
      }
    } catch (error) {
      console.error('Failed to provision phone:', error);
      setProvisioningStatus('');
      if (error.response?.status === 401 || error.response?.status === 403) {
        onLogout();
        return;
      }
      
      // Enhanced error message with attempted area codes
      let errorMessage = error.response?.data?.error || t('setupWizard.failedToProvisionPhone');
      if (error.response?.data?.attemptedAreaCodes) {
        errorMessage += `\n\n${t('setupWizard.attemptedAreaCodes', { areaCodes: error.response.data.attemptedAreaCodes.join(', ') })}`;
      }
      
      setModal({
        isOpen: true,
        title: t('common.error'),
        message: errorMessage,
        type: 'error',
        onConfirm: null,
        confirmText: t('common.ok'),
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 2) {
      handleCreateAssistant();
    } else if (currentStep === 3) {
      handleProvisionPhone();
    } else if (currentStep === 4) {
      setCurrentStep(5);
    } else if (currentStep === 5) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const currentStepData = steps.find(step => step.id === currentStep);
  const isLastStep = currentStep === steps.length;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col h-screen">
      <div className="flex flex-col w-full h-full">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('setupWizard.welcomeToClinic', { clinicName: clinic?.clinic_name })}</h1>
              <p className="text-gray-600 mt-1">{t('setupWizard.letsSetupAssistant')}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {t('setupWizard.stepOf', { current: currentStep, total: steps.length })}
              </div>
              <div className="flex space-x-2">
                {steps.map((step) => (
                  <div
                    key={step.id}
                    className={`w-4 h-4 rounded-full ${
                      step.id <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center px-8 py-8 min-h-0 overflow-auto">
          <div className="w-full max-w-4xl">
            {currentStepData.content}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              disabled={loading}
              className="px-8 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('setupWizard.pleaseWait') : (isLastStep ? t('setupWizard.completeSetup') : t('common.continue'))}
            </button>
          </div>
        </div>

        {/* Modal */}
        <Modal
          isOpen={modal.isOpen}
          onClose={() => setModal({ ...modal, isOpen: false })}
          title={modal.title}
          message={modal.message}
          type={modal.type}
          onConfirm={modal.onConfirm}
          confirmText={modal.confirmText}
          showCancel={modal.showCancel}
        />
      </div>
    </div>
  );
};

export default SetupWizard;
